import { useCallback, useMemo, useRef } from "react";
import {
  AppToasterV2,
  CoreFieldValue,
  FieldFormV2Module,
  useFieldSchemaV2FactoryContext,
} from "@ucl/library";
import { DepositApplicationReadDTO, FinancialApplication } from "../types/FinancialApplication";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { cloneDeep } from "lodash";
import { AxiosError } from "axios";
import { useAutoSave } from "./useAutoSave";
import { createFinancialApplicationFormBuilder } from "@/builders/financialApplicationFormBuilder";
import { financialApplicationAtomFamily } from "@/atoms/financialApplicationAtomFamily";
import { useAtomValue, useSetAtom } from "jotai";
import { useFinancialApplicationRealtime } from "./useFinancialApplicationRealtime";
import { FinancialApplicationFormSectionContextType } from "./useFinancialApplicationFormSectionValidation";

/**
 * Hook to manage the financial application form
 * @param financialApplicationId - The ID of the financial application
 * @returns An object containing the form value, setFormValue, saveFormData, isSaving, isLoading, schemaFactory, formBuilder, and fieldFormModule
 */
export const useFinancialApplicationForm = (
  financialApplicationId: string,
  executionContextType: FinancialApplicationFormSectionContextType = FinancialApplicationFormSectionContextType.Applicant
) => {
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);

  const fieldFormModule = useRef<FieldFormV2Module<FinancialApplication> | null>(null);

  const financialApplicationFormState = useAtomValue(
    financialApplicationAtomFamily(financialApplicationId)
  );

  const setFinancialApplicationFromForm = useSetAtom(
    financialApplicationFormState.setFinancialApplicationFromForm
  );
  const setFinancialApplicationFromRemote = useSetAtom(
    financialApplicationFormState.setFinancialApplicationFromRemote
  );

  const formValue = useAtomValue(financialApplicationFormState.financialApplication);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (formValue) {
        await fieldFormModule.current?.submit();
      }
    },
    debounceDelay: 1000,
  });

  const refreshFinancialApplication = useCallback(async () => {
    const financialApplication =
      await financialApplicationApiClient.getFinancialApplication(financialApplicationId);
    await setFinancialApplicationFromRemote(financialApplication, undefined);
    return financialApplication;
  }, [financialApplicationApiClient, financialApplicationId, setFinancialApplicationFromRemote]);

  useFinancialApplicationRealtime(financialApplicationId);

  const setFormValue = async (
    value: FinancialApplication,
    _newFieldValue: CoreFieldValue,
    submitAlways: boolean = false
  ) => {
    const changed = await setFinancialApplicationFromForm(value);
    if (changed || submitAlways) {
      // Mark as dirty and trigger auto-save
      markDirty();
    }
  };

  const runAfterSaveCallbacks = async () => {
    const currentState = financialApplicationFormState;
    while (currentState.afterSaveQueue.length > 0) {
      const callback = currentState.afterSaveQueue.shift();
      await callback?.();
    }
  };

  const enqueueAfterNextSave = (callback: () => Promise<any>) => {
    const currentState = financialApplicationFormState;
    currentState.afterSaveQueue.push(callback);
  };

  const saveFormData = async (data: FinancialApplication) => {
    const savedValue = cloneDeep(data);
    const currentState = financialApplicationFormState;
    if (currentState.isSaving) {
      enqueueAfterNextSave(async () => {
        await saveFormData(savedValue);
      });

      return;
    }

    currentState.isSaving = true;

    let saved = false;

    try {
      const updatedApplication = (await financialApplicationApiClient.updateFinancialApplication(
        financialApplicationId,
        savedValue
      )) as FinancialApplication;

      // If the API returns the updated FinancialApplication with fresh metrics, hydrate the atom
      if (updatedApplication) {
        await setFinancialApplicationFromRemote(updatedApplication, savedValue);
      }

      saved = true;
    } catch (error) {
      // Skip 400 errors, validation is handled by the form
      if (error instanceof AxiosError && error.response?.status === 400) {
        return;
      }
      console.error("Error saving financial application data:", error);

      AppToasterV2.showFailedActionToaster("Failed to save financial application data.");
    } finally {
      currentState.isSaving = false;

      // We must set isSavingThisFinancialApplication to false before running the after save queue, in case further saves are enqueued.
      try {
        if (saved) {
          await runAfterSaveCallbacks();
        }
      } catch (error) {
        console.error("Error running after save queue:", error);
      }
    }
  };

  const saveFinancialApplicationDocuments = async (data: FinancialApplication) => {
    const savedValue = cloneDeep(data);
    const currentState = financialApplicationFormState;
    if (currentState.isSaving) {
      enqueueAfterNextSave(async () => {
        await saveFinancialApplicationDocuments(savedValue);
      });

      return;
    }

    currentState.isSaving = true;

    let saved = false;

    try {
      const updatedApplication =
        (await financialApplicationApiClient.updateFinancialApplicationDocuments(
          financialApplicationId,
          savedValue
        )) as FinancialApplication;

      // If the API returns the updated FinancialApplication with fresh metrics, hydrate the atom
      if (updatedApplication) {
        await setFinancialApplicationFromRemote(updatedApplication, savedValue);
      }

      saved = true;
    } catch (error) {
      // Skip 400 errors, validation is handled by the form
      if (error instanceof AxiosError && error.response?.status === 400) {
        return;
      }
      console.error("Error saving financial application data:", error);

      AppToasterV2.showFailedActionToaster("Failed to save financial application data.");
    } finally {
      currentState.isSaving = false;

      // We must set isSavingThisFinancialApplication to false before running the after save queue, in case further saves are enqueued.
      try {
        if (saved) {
          await runAfterSaveCallbacks();
        }
      } catch (error) {
        console.error("Error running after save queue:", error);
      }
    }
  };

  const updateDepositApplicationAmount = (depositApplicationId: string, amount: number, field: 'initialDepositAmount' | 'creditAmountRequested' | 'creditAmountOffered' | 'interestRateSpreadInBasisPoints') => {
    if (!formValue) return;

    // Update the depositApplications array with the new amount
    const updatedDepositApplications = formValue.depositApplications.map(
      (depositApp: DepositApplicationReadDTO) =>
        depositApp.id === depositApplicationId
          ? { ...depositApp, [field]: amount }
          : depositApp
    );

    // Create updated form value
    const updatedFormValue = {
      ...formValue,
      depositApplications: updatedDepositApplications,
    };

    setFinancialApplicationFromForm(updatedFormValue);

    // Mark as dirty and trigger auto-save
    markDirty();
  };

  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  const formBuilder = useMemo(() => {
    if (!schemaFactory) return undefined;
    return createFinancialApplicationFormBuilder(schemaFactory, executionContextType);
  }, [schemaFactory]);

  return {
    formValue,
    setFormValue,
    saveFormData,
    saveFinancialApplicationDocuments,
    schemaFactory,
    formBuilder,
    fieldFormModule,
    enqueueAfterNextSave,
    refreshFinancialApplication,
    updateDepositApplicationAmount,
  };
};
